/**
 * Financial Resilience — Insights Page
 *
 * Answers: "Can we financially survive a cyber event, and are we adequately protected?"
 * Tabbed layout: Loss Scenarios / Capital Adequacy / Insurance Optimization
 */

import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../../../shared/lib/supabase'
import { useInsightsData } from '../hooks/useInsightsData'
import { simulateAggregateExposure } from '../../../shared/utils/monteCarloSimulation'
import { calculateInsuranceAdequacy } from '../../../shared/utils/insuranceAdequacy'
import InsightCard from '../components/InsightCard'
import InsuranceAdequacyCard from '../components/InsuranceAdequacyCard'
import ImpactCalibrationStep from '../../assessment/components/ImpactCalibrationStep'
import { useAssessment } from '../../../contexts/AssessmentContext'
import { calculateScores } from '../../../shared/utils/scoring'

function fmt(v) {
  if (v == null || isNaN(v)) return '$0'
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`
  if (v >= 1000) return `$${Math.round(v / 1000)}K`
  return `$${Math.round(v)}`
}

function FinancialResilience({ assessmentId, client }) {
  // Fetch critical systems for BIA-informed calculations
  const [criticalSystems, setCriticalSystems] = useState(null)
  useEffect(() => {
    if (!client?.id) return
    supabase.from('client_critical_systems').select('*').eq('client_id', client.id)
      .then(({ data }) => setCriticalSystems((data || []).filter(s => s.hourly_cost_impact != null)))
  }, [client?.id])

  const { data, raw, loading, error } = useInsightsData(assessmentId, { criticalSystems })
  const { getAssessment, saveResponse } = useAssessment()
  const { assessment } = getAssessment(assessmentId) || {}
  const [activeTab, setActiveTab] = useState('scenarios')
  const [selectedScenarioIds, setSelectedScenarioIds] = useState(null) // null = all selected (before initialization)

  const handleSave = (key, data) => {
    if (!assessmentId) return
    saveResponse(assessmentId, key, null, data)
  }

  // Compute maturity score for ImpactCalibrationStep
  const maturityScore = (() => {
    if (!assessment?.responses?.maturityAssessment) return 50
    try {
      const scores = calculateScores(assessment.responses)
      return scores.overall?.score || 50
    } catch { return 50 }
  })()

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>
  if (error || !data) return <div className="text-center py-12 text-gray-500 dark:text-gray-400">Unable to load financial resilience analysis.</div>

  const { riskAndThreats } = data
  const allScenarios = raw?.lossScenarios?.scenarios || []
  const rawImpactData = assessment?.responses?.impactData || assessment?.responses?.notes?.impactData || {}
  const dynamicCalibration = raw?.dynamicCalibration || {}

  // Filter scenarios based on user selection
  const activeScenarios = useMemo(() => {
    if (!selectedScenarioIds) return allScenarios // null = not yet initialized, use all
    return allScenarios.filter(s => selectedScenarioIds[s.scenarioId])
  }, [allScenarios, selectedScenarioIds])

  const selectionCount = selectedScenarioIds ? Object.keys(selectedScenarioIds).length : allScenarios.length
  const isFiltered = selectedScenarioIds !== null && selectionCount < allScenarios.length && selectionCount > 0

  // Recompute Monte Carlo and insurance with only selected scenarios
  const { mc, insurance } = useMemo(() => {
    // If all selected or no selection yet, use original results
    if (!isFiltered) {
      return {
        mc: raw?.monteCarloResults || null,
        insurance: raw?.insuranceAdequacy || riskAndThreats.insurance || {}
      }
    }
    // Re-run Monte Carlo with filtered scenarios
    let filteredMc = null
    try {
      if (activeScenarios.length > 0) {
        filteredMc = simulateAggregateExposure(activeScenarios, dynamicCalibration, rawImpactData, 10000)
      }
    } catch { /* incomplete data */ }

    // Re-run insurance adequacy with filtered scenarios
    const filteredInsurance = calculateInsuranceAdequacy(
      rawImpactData,
      { scenarios: activeScenarios },
      filteredMc || {}
    )

    return { mc: filteredMc, insurance: filteredInsurance }
  }, [activeScenarios, isFiltered, raw?.monteCarloResults, raw?.insuranceAdequacy, riskAndThreats.insurance, dynamicCalibration, rawImpactData])

  const impactData = rawImpactData

  const coverageLimit = insurance.coverageLimit || 0
  const deductible = insurance.deductible || 10000
  const hasInsurance = coverageLimit > 0
  const reserves = impactData.cyber_reserves || 0
  const annualRevenue = impactData.estimated_annual_revenue || 0

  // Ruin analysis from Monte Carlo
  const mcPercentiles = mc?.aggregateDistribution?.percentiles || {}
  const probExceedingCoverage = insurance.probabilityOfExceedingCoverage || 0
  const p50 = mcPercentiles.p50 || 0
  const p75 = mcPercentiles.p75 || 0
  const p90 = mcPercentiles.p90 || 0
  const p95 = mcPercentiles.p95 || 0

  // Reserve stress calculations
  const totalProtection = coverageLimit + reserves
  const exceedanceCurve = mc?.exceedanceCurve || []
  const probExceedingReserves = exceedanceCurve.find(e => e.threshold >= reserves)?.probability || 0
  const probExceedingTotal = exceedanceCurve.find(e => e.threshold >= totalProtection)?.probability || 0

  // Recommended reserve to cover 90% of scenarios
  const reserveFor90 = Math.max(p90 - coverageLimit + deductible, deductible * 2)
  const reserveFor95 = Math.max(p95 - coverageLimit + deductible, deductible * 3)

  // Cash flow impact
  const margin = 0.10 // Assume 10% net margin
  const monthlyFreeCash = (annualRevenue * margin) / 12
  const recoveryMonthsP50 = monthlyFreeCash > 0 ? Math.ceil(Math.max(0, p50 - coverageLimit) / monthlyFreeCash) : 0
  const recoveryMonthsP75 = monthlyFreeCash > 0 ? Math.ceil(Math.max(0, p75 - coverageLimit) / monthlyFreeCash) : 0
  const recoveryMonthsP90 = monthlyFreeCash > 0 ? Math.ceil(Math.max(0, p90 - coverageLimit) / monthlyFreeCash) : 0

  const tabs = [
    { id: 'scenarios', label: 'Loss Scenarios' },
    { id: 'adequacy', label: 'Capital Adequacy' },
    { id: 'insurance', label: 'Insurance Optimization' }
  ]

  return (
    <div className="px-1 py-2">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Financial Resilience</h1>
        <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">
          Capital adequacy, loss exposure, and insurance coverage analysis
        </p>
      </div>

      {/* Executive paragraph */}
      <div className="mb-6 mt-4">
        <p className="text-[13px] text-gray-600 dark:text-gray-400 leading-relaxed">
          Based on {client?.name ? <span className="font-medium text-gray-800 dark:text-gray-200">{client.name}</span> : "this organization"}'s
          current security posture{hasInsurance && <> and <span className="font-medium text-gray-800 dark:text-gray-200">{fmt(coverageLimit)}</span> cyber insurance coverage</>},
          {probExceedingCoverage > 0 && <> there is a <span className="font-medium text-red-600 dark:text-red-400">{Math.round(probExceedingCoverage * 100)}% probability</span> of losses exceeding coverage in a given year</>}.
          {reserves > 0 && <> With <span className="font-medium text-gray-800 dark:text-gray-200">{fmt(reserves)}</span> in designated cyber reserves</>}
          {deductible > 0 && <>{reserves > 0 ? ' and' : ' With'} a <span className="font-medium text-gray-800 dark:text-gray-200">{fmt(deductible)}</span> policy deductible</>},
          the first response costs are self-funded.
        </p>
      </div>

      {/* Tab bar */}
      <div className="border-b border-gray-200 dark:border-gray-700 mt-4">
        <nav className="flex gap-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium transition-colors relative ${
                activeTab === tab.id
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400 rounded-full" />
              )}
            </button>
          ))}
        </nav>
      </div>

      <div className="pt-6" />

      {/* Tab 1: Loss Scenarios */}
      {activeTab === 'scenarios' && (
        <div className="space-y-5">
          {/* Full Impact Calibration Loss Scenario Analysis */}
          {assessment?.responses ? (
            <ImpactCalibrationStep
              responses={assessment.responses}
              onSave={handleSave}
              maturityScore={maturityScore}
              client={client}
              showOnly="lossScenarios"
              selectedScenarioIds={selectedScenarioIds}
              onSelectionChange={setSelectedScenarioIds}
            />
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <p className="text-sm">Complete threat context and impact calibration to generate loss scenarios.</p>
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Capital Adequacy */}
      {activeTab === 'adequacy' && (
        <div className="space-y-5">
          {/* Filter indicator */}
          {isFiltered && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-lg">
              <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Analysis reflects <span className="font-semibold">{selectionCount} of {allScenarios.length}</span> loss scenarios.
                <button onClick={() => setActiveTab('scenarios')} className="ml-1 underline hover:no-underline">Adjust selection</button>
              </p>
            </div>
          )}

          {/* Full Impact Calibration Ruin Theory Analysis */}
          {assessment?.responses && (
            <ImpactCalibrationStep
              responses={assessment.responses}
              onSave={handleSave}
              maturityScore={maturityScore}
              client={client}
              showOnly="ruinTheory"
              selectedScenarioIds={selectedScenarioIds}
              onSelectionChange={setSelectedScenarioIds}
            />
          )}

          {mc ? (
            <>
              {/* Ruin probability */}
              <InsightCard title="Ruin Probability Analysis" subtitle="Likelihood that cyber losses exceed your financial capacity" accent="#EF4444">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Prob. Exceeding Reserves</p>
                      <p className={`text-2xl font-bold ${probExceedingReserves > 0.3 ? 'text-red-600 dark:text-red-400' : probExceedingReserves > 0.1 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                        {Math.round(probExceedingReserves * 100)}%
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Reserves: {fmt(reserves || deductible)}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Prob. Exceeding Coverage</p>
                      <p className={`text-2xl font-bold ${probExceedingCoverage > 0.2 ? 'text-red-600 dark:text-red-400' : probExceedingCoverage > 0.05 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                        {Math.round(probExceedingCoverage * 100)}%
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Coverage: {fmt(coverageLimit)}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Prob. Exceeding Total Protection</p>
                      <p className={`text-2xl font-bold ${probExceedingTotal > 0.1 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                        {Math.round(probExceedingTotal * 100)}%
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Reserves + Coverage: {fmt(totalProtection)}</p>
                    </div>
                  </div>
                </div>
              </InsightCard>

              {/* Cash flow impact */}
              {annualRevenue > 0 && (
                <InsightCard title="Cash Flow Recovery" subtitle="Estimated months to recover from uninsured losses based on operating margin" accent="#3B82F6">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Median event</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{recoveryMonthsP50 || '<1'}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">months to recover</p>
                    </div>
                    <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Likely severe</p>
                      <p className={`text-2xl font-bold ${recoveryMonthsP75 > 6 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>{recoveryMonthsP75 || '<1'}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">months to recover</p>
                    </div>
                    <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Severe event</p>
                      <p className={`text-2xl font-bold ${recoveryMonthsP90 > 12 ? 'text-red-600 dark:text-red-400' : recoveryMonthsP90 > 6 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>{recoveryMonthsP90 || '<1'}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">months to recover</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-3 text-center">
                    Based on {fmt(annualRevenue)} annual revenue with an assumed 10% operating margin ({fmt(monthlyFreeCash)}/month available for recovery)
                  </p>
                </InsightCard>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <p className="text-sm">Complete impact calibration to enable capital adequacy analysis.</p>
            </div>
          )}
        </div>
      )}

      {/* Tab 3: Insurance Optimization */}
      {activeTab === 'insurance' && (
        <div className="space-y-5">
          {/* Filter indicator */}
          {isFiltered && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-lg">
              <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Analysis reflects <span className="font-semibold">{selectionCount} of {allScenarios.length}</span> loss scenarios.
                <button onClick={() => setActiveTab('scenarios')} className="ml-1 underline hover:no-underline">Adjust selection</button>
              </p>
            </div>
          )}

          {/* Full insurance adequacy */}
          <InsightCard title="Coverage Assessment">
            <InsuranceAdequacyCard adequacy={insurance} />
          </InsightCard>

          {/* Per-scenario coverage */}
          {insurance.scenarioAdequacy?.length > 0 && (
            <InsightCard title="Per-Scenario Coverage" subtitle="How your policy covers each loss event type">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <th className="pb-3 pr-4">Scenario</th>
                      <th className="pb-3 pr-4">Expected Loss</th>
                      <th className="pb-3 pr-4">Covered Amount</th>
                      <th className="pb-3 pr-4">Gap</th>
                      <th className="pb-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30">
                    {insurance.scenarioAdequacy.map(sa => (
                      <tr key={sa.scenarioId}>
                        <td className="py-3 pr-4 text-gray-900 dark:text-white font-medium">{sa.scenarioName}</td>
                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">{fmt(sa.expectedLoss)}</td>
                        <td className="py-3 pr-4 text-green-600 dark:text-green-400">{fmt(sa.coveredAmount)}</td>
                        <td className="py-3 pr-4">
                          {sa.gap > 0 ? <span className="text-red-600 dark:text-red-400 font-medium">{fmt(sa.gap)}</span> : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="py-3">
                          {sa.adequate ? (
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">Adequate</span>
                          ) : (
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">Gap</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </InsightCard>
          )}

          {/* Reserve stress test */}
          {mc && (() => {
            const recCoverage = insurance.recommendedCoverage || coverageLimit
            const showComparison = recCoverage > coverageLimit

            return (
              <InsightCard
                title="Reserve Stress Test"
                subtitle={showComparison
                  ? `Current coverage (${fmt(coverageLimit)}) vs recommended (${fmt(recCoverage)})`
                  : 'What reserve levels are needed to cover various loss percentiles'
                }
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        <th className="pb-3 pr-4">Scenario</th>
                        <th className="pb-3 pr-4">Gross Loss</th>
                        <th className="pb-3 pr-4">Current Coverage</th>
                        <th className="pb-3 pr-4">Out-of-Pocket</th>
                        {showComparison && <th className="pb-3">With Recommended</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30">
                      {[
                        { label: 'Median event (p50)', loss: p50 },
                        { label: 'Likely severe (p75)', loss: p75 },
                        { label: 'Severe (p90)', loss: p90 },
                        { label: 'Worst case (p95)', loss: p95 }
                      ].map(row => {
                        const insured = hasInsurance ? Math.min(Math.max(0, row.loss - deductible), coverageLimit - deductible) : 0
                        const oop = Math.max(0, row.loss - insured)
                        const recInsured = hasInsurance ? Math.min(Math.max(0, row.loss - deductible), recCoverage - deductible) : 0
                        const recOop = Math.max(0, row.loss - recInsured)

                        return (
                          <tr key={row.label}>
                            <td className="py-3 pr-4 text-gray-900 dark:text-white font-medium">{row.label}</td>
                            <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">{fmt(row.loss)}</td>
                            <td className="py-3 pr-4 text-green-600 dark:text-green-400">{fmt(insured)}</td>
                            <td className={`py-3 pr-4 font-semibold ${oop > reserves ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                              {fmt(oop)}
                            </td>
                            {showComparison && (
                              <td className="py-3">
                                <span className={`font-medium ${recOop < oop ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-300'}`}>
                                  {fmt(recOop)}
                                </span>
                                {recOop < oop && (
                                  <span className="text-[10px] text-green-500 ml-1">({fmt(oop - recOop)} less)</span>
                                )}
                              </td>
                            )}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </InsightCard>
            )
          })()}

          {/* Coverage optimization recommendations */}
          {insurance.recommendations?.length > 0 && (
            <InsightCard title="Optimization Recommendations" accent="#1D9E75">
              <div className="space-y-3">
                {insurance.recommendations.map((rec, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5 ${
                      rec.priority === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                      : rec.priority === 'medium' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                      : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    }`}>
                      {idx + 1}
                    </span>
                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{rec.action}</p>
                      {rec.estimatedCostIncrease && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Estimated cost: {rec.estimatedCostIncrease}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </InsightCard>
          )}

          {/* Reserve recommendation */}
          {insurance.reserveRecommendation && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/15 rounded-lg border border-blue-100 dark:border-blue-800/30">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                    Recommended reserve: {fmt(insurance.reserveRecommendation.recommendedReserve || insurance.reserveRecommendation)}
                  </p>
                  {insurance.reserveRecommendation.reason && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">{insurance.reserveRecommendation.reason}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <p className="text-[10px] text-gray-400 dark:text-gray-500 italic mt-4">
            Insurance estimates are for planning purposes only. Actual coverage is subject to policy limitations, exclusions, sub-limits, and waiting periods. Review all coverage details with your insurance carrier or a qualified insurance professional.
          </p>
        </div>
      )}
    </div>
  )
}

export default FinancialResilience
